import React, { useEffect, useState } from 'react';
import VideoForm from '../components/VideoForm';
import AdminSidebar from '../components/Admin/layout/AdminSidebar';
import AdminVideos from '../components/Admin/AdminVideos';
import AdminSocialSettings from '../components/Admin/AdminSocialSettings';
import AdminContactSettings from '../components/Admin/AdminContactSettings';
import AdminLegalSettings from '../components/Admin/AdminLegalSettings';
import AdminAppDownloadSettings from '../components/Admin/AdminAppDownloadSettings';
import * as Icons from 'react-icons/fi';

// Custom Hooks
import { useVideos } from '../hooks/useVideos';
import { useSettings } from '../hooks/useSettings';
import { usePasswordChange } from '../hooks/usePasswordChange';

// Styled Components - Layout
import {
  AdminContainer,
  AdminWrapper,
  TopHeader,
  Logo,
  SearchBar,
  SearchInput,
  SearchIcon,
  UserMenu,
  UserAvatar,
  MenuToggle,
  MainContent,
  Section,
  SectionHeader,
  SectionTitle,
  Modal,
  ModalContent
} from '../styles/AdminLayout.styles';

// Styled Components - Form
import {
  SocialForm,
  FormGroup,
  FormLabel,
  FormInput,
  FormActions,
  SaveBtn
} from '../styles/AdminForm.styles';

const AdminContent = () => {
  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('videos');
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('Todos');

  // Custom Hooks
  const {
    videos,
    loading: videosLoading,
    error: videosError,
    showForm,
    editingVideo,
    fetchVideos,
    handleCreate,
    handleEdit,
    handleDelete,
    handleCloseForm
  } = useVideos();

  const {
    socialSettings,
    setSocialSettings,
    fetchSettings,
    handleSaveSocialSettings
  } = useSettings();

  const {
    passwordData,
    setPasswordData,
    handleChangePassword
  } = usePasswordChange();

  // Effects
  useEffect(() => {
    fetchVideos();
    fetchSettings();
  }, [fetchVideos, fetchSettings]);

  return (
    <AdminContainer>
      <AdminWrapper>
        {/* Header */}
        <TopHeader>
          <Logo>
            {React.createElement((Icons as any).FiVideo, { size: 24 })}
            Panel de Administración
          </Logo>
          <MenuToggle onClick={() => setSidebarOpen(true)}>
            {React.createElement((Icons as any).FiMenu, { size: 24 })}
          </MenuToggle>
          <SearchBar>
            <SearchIcon>
              {React.createElement((Icons as any).FiSearch, { size: 16 })}
            </SearchIcon>
            <SearchInput
              placeholder="Buscar vídeos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchBar>
          <UserMenu>
            <UserAvatar>A</UserAvatar>
          </UserMenu>
        </TopHeader>

        {/* Sidebar */}
        <AdminSidebar
          isOpen={sidebarOpen}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Main Content */}
        <MainContent>
          {/* Videos Section */}
          {activeSection === 'videos' && (
            <AdminVideos
              videos={videos}
              loading={videosLoading}
              error={videosError}
              searchQuery={searchQuery}
              levelFilter={levelFilter}
              onSearchChange={setSearchQuery}
              onLevelFilterChange={setLevelFilter}
              onCreateVideo={handleCreate}
              onEditVideo={handleEdit}
              onDeleteVideo={handleDelete}
            />
          )}

          {/* Social Settings Section */}
          {activeSection === 'social' && (
            <AdminSocialSettings
              settings={socialSettings}
              onUpdate={setSocialSettings}
              onSave={handleSaveSocialSettings}
            />
          )}

          {/* Contact Settings Section */}
          {activeSection === 'contact' && (
            <AdminContactSettings
              settings={socialSettings}
              onUpdate={setSocialSettings}
              onSave={handleSaveSocialSettings}
            />
          )}

          {/* Legal Settings Section */}
          {activeSection === 'legal' && (
            <AdminLegalSettings
              settings={socialSettings}
              onUpdate={setSocialSettings}
              onSave={handleSaveSocialSettings}
            />
          )}

          {/* App Download Settings Section */}
          {activeSection === 'app-download' && (
            <AdminAppDownloadSettings
              settings={socialSettings}
              onUpdate={setSocialSettings}
              onSave={handleSaveSocialSettings}
            />
          )}

          {/* Password Change Section */}
          {activeSection === 'password' && (
            <Section>
              <SectionHeader>
                <SectionTitle>
                  {React.createElement((Icons as any).FiLock, { size: 24 })}
                  Cambiar Contraseña
                </SectionTitle>
              </SectionHeader>

              <SocialForm onSubmit={handleChangePassword}>
                <FormGroup>
                  <FormLabel>Contraseña Actual</FormLabel>
                  <FormInput
                    type="password"
                    value={passwordData.oldPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, oldPassword: e.target.value }))}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel>Nueva Contraseña</FormLabel>
                  <FormInput
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    required
                    minLength={5}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel>Confirmar Nueva Contraseña</FormLabel>
                  <FormInput
                    type="password"
                    value={passwordData.confirmNewPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmNewPassword: e.target.value }))}
                    required
                    minLength={5}
                  />
                </FormGroup>

                <FormActions>
                  <SaveBtn type="submit">
                    {React.createElement((Icons as any).FiSave, { size: 18 })}
                    Actualizar Contraseña
                  </SaveBtn>
                </FormActions>
              </SocialForm>
            </Section>
          )}
        </MainContent>

        {/* Video Form Modal */}
        {showForm && (
          <Modal>
            <ModalContent>
              <VideoForm video={editingVideo || null} onClose={handleCloseForm} />
            </ModalContent>
          </Modal>
        )}
      </AdminWrapper>
    </AdminContainer>
  );
};

export default AdminContent;